import { ICryptoWallet, ICryptoWalletCreate } from "@novel/shared/interfaces/CryptoWallet";
import {
    getSignedEvmMessage,
    getSignedSolanaMessage,
} from "@novel/shared/utils/verifyAddress/getSignedMessage";
import { retryWithBackoff } from "@novel/shared/utils/retryWithBackoff";
import { ICryptoAddressCreateSigned } from "@novel/shared/interfaces/CryptoAddress";
import { makeErrorSurfaceable, SurfaceableError } from "@novel/shared/utils/SurfaceableError";
import {
    clearWalletConnected,
    clearWalletConnectIsConnecting,
    disconnectWalletV2Connect,
    getCoinbaseInstance,
    getMetamaskInstance,
    getPhantomInstance,
    getTorusInstance,
    getTorusUserInfo,
    getWalletConnectInstance,
    getWalletConnectV2Instance,
    isWalletConnectConnected,
    isWalletConnectV2Connected,
    isWalletExtensionInstalled,
    redirectToCoinbaseApp,
    redirectToMetaMaskApp,
    redirectToPhantomApp,
    walletConnectConnection,
} from "@novel/shared/utils/wallets/walletLibInstances";
import type { UserInfo } from "@toruslabs/torus-embed";
import { checkIsChrome, checkIsMobile } from "@novel/shared/utils/userAgentUtils";
import { openInNewTab } from "@novel/shared/utils/locationUtils";
import { blockchainAddressEquals } from "@novel/shared/utils/blockchainHashEquals";

export interface IAuthWallet {
    walletProvider: WalletProviderType;
    wallet?: ICryptoWallet;
    ensureWalletAppInstalled<T>(makeReq: (wallet: this) => Promise<T>): Promise<T | undefined>;
    authenticate: () => Promise<ICryptoWalletCreate>;
}

export type WalletProviderType =
    | "metamask"
    | "wallet-connect"
    | "coinbase"
    | "torus"
    | "phantom"
    | "wallet-connect-v2";

export class AuthWallet implements IAuthWallet {
    walletProvider: WalletProviderType;

    preMobileAppRedirectHook?: () => string;

    wallet?: ICryptoWallet;

    constructor(
        walletProvider: WalletProviderType | ICryptoWallet,
        preMobileAppRedirectHook?: () => string,
    ) {
        if (typeof walletProvider === "string") {
            this.walletProvider = walletProvider;
        } else {
            this.walletProvider = walletProvider.connectionApp;
            this.wallet = walletProvider;
        }

        this.preMobileAppRedirectHook = preMobileAppRedirectHook;
    }

    async authenticate(): Promise<ICryptoWalletCreate> {
        try {
            return await this._authenticate();
        } catch (e: any) {
            // exit if user chose not to sign transaction
            const errorMessage: string | undefined = e.message?.toLowerCase();
            const isUserClosedPopupError = errorMessage?.includes("user ");
            if (isUserClosedPopupError) {
                throw makeErrorSurfaceable(e);
            }
            const alreadyClicked = errorMessage?.includes("already processing eth_requestaccounts");
            if (alreadyClicked) {
                const isMobileBrowser = checkIsMobile();
                const isExtension = !isMobileBrowser && this.walletProvider !== "wallet-connect";
                throw new SurfaceableError(
                    `Connection in progress, open your wallet${!isExtension ? ` app` : ""}`,
                );
            }
            throw e;
        }
    }

    async ensureWalletAppInstalled<T>(
        makeReq: (wallet: this) => Promise<T>,
    ): Promise<T | undefined> {
        const isInstalled = isWalletExtensionInstalled(this.walletProvider);
        if (this.walletProvider === "metamask") {
            if (!isInstalled) {
                if (checkIsMobile()) {
                    redirectToMetaMaskApp(this.preMobileAppRedirectHook?.());
                    return;
                } else if (checkIsChrome()) {
                    openInNewTab(
                        "https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn",
                    );
                    return;
                } else {
                    openInNewTab("https://metamask.io/download");
                    return;
                }
            }
        } else if (this.walletProvider === "coinbase") {
            // on mobile we do a deep link redirect to the coinbase browser
            if (!isInstalled) {
                if (checkIsMobile()) {
                    redirectToCoinbaseApp(this.preMobileAppRedirectHook?.());
                    return;
                }
            }
        } else if (this.walletProvider === "phantom") {
            if (!isInstalled) {
                if (checkIsMobile()) {
                    redirectToPhantomApp(this.preMobileAppRedirectHook?.());
                    return;
                } else if (checkIsChrome()) {
                    openInNewTab(
                        "https://chrome.google.com/webstore/detail/phantom/bfnaelmomeimhlpmgjnjophhpkkoljpa",
                    );
                    return;
                } else {
                    openInNewTab("https://phantom.app/download");
                    return;
                }
            }
        }

        return makeReq(this);
    }

    // requires wallet app to be installed
    // to be "connected" on the client side (for non-extensions like Torus and Wallet Connect this is a separate step, extensions it's not)
    async ensureClientSideWalletConnection(): Promise<void> {
        switch (this.walletProvider) {
            case "torus": {
                const { client: torusClient } = await getTorusInstance();
                let { isLoggedIn } = torusClient;
                if (
                    !isLoggedIn ||
                    (this.wallet && torusClient.currentVerifier !== this.wallet?.verifier)
                ) {
                    isLoggedIn = false;
                    try {
                        await torusClient.logout();
                        // eslint-disable-next-line no-empty
                    } catch (e: any) {}
                }

                if (!isLoggedIn) {
                    await retryWithBackoff(
                        () =>
                            this.wallet
                                ? torusClient.login({
                                      verifier: this.wallet.verifier,
                                      login_hint: this.wallet.verifierEmail,
                                  })
                                : torusClient.login(),
                        // no retry if user closed
                        (e) => e.message?.toLowerCase()?.includes("user "),
                        0,
                    );
                }

                if (this.wallet) {
                    const userInfo: UserInfo = await getTorusUserInfo(torusClient);
                    if (this.wallet.verifierId !== userInfo.verifierId) {
                        await torusClient.logout();
                        throw new SurfaceableError(
                            `Logged into account for ${userInfo.verifierId}, please log into ${this.wallet.verifierId} in order to access this wallet`,
                        );
                    }
                }

                break;
            }
            case "coinbase": {
                const { provider: coinbaseProvider } = getCoinbaseInstance();
                await coinbaseProvider.enable().catch((err) => {
                    coinbaseProvider.disconnect();
                    throw err;
                });

                break;
            }

            case "metamask": {
                const { provider } = await getMetamaskInstance();
                /// this will trigger when account is not connected
                await provider.request({ method: "eth_requestAccounts" });
                break;
            }

            case "wallet-connect-v2": {
                const { accounts, session } = await getWalletConnectV2Instance();

                let AlreadyConnected = isWalletConnectV2Connected();
                if (AlreadyConnected) {
                    const constructorWalletAddresses = (
                        this.wallet?.cryptoAddresses.map((a) => a.address) || []
                    ).sort();

                    const walletConnectSessionAddresses = (accounts || []).slice().sort();

                    const addressesMatch =
                        !!constructorWalletAddresses.length &&
                        constructorWalletAddresses.every(
                            (address, i) =>
                                walletConnectSessionAddresses[i] &&
                                blockchainAddressEquals(address, walletConnectSessionAddresses[i]),
                        );

                    if (!addressesMatch || !session) {
                        await disconnectWalletV2Connect();
                        AlreadyConnected = false;
                    }
                }

                if (!AlreadyConnected) {
                    await walletConnectConnection(undefined);
                }

                break;
            }

            case "wallet-connect": {
                const { provider } = await getWalletConnectInstance();

                if (provider.isConnecting) {
                    clearWalletConnectIsConnecting(provider);
                }

                let isAlreadyConnected = isWalletConnectConnected(provider);

                if (isAlreadyConnected) {
                    // wallet connect does a cool thing where it prompts the user directly
                    // in their app to sign, but if there are no push notifications enabled
                    // then it provides a confusing experience, our workaround is to still force
                    // connecting if they have not connected within this browsing session
                    // TODO: optimize toast messages aronud this case to avoid re-scan
                    const connectedWithinSession = !!(window as any)
                        .__wallet_connect_within_session__;

                    const constructorWalletAddresses = (
                        this.wallet?.cryptoAddresses.map((a) => a.address) || []
                    ).sort();

                    const walletConnectSessionAddresses = (provider.accounts || []).slice().sort();

                    // if no wallet was provided we assume that the user is trying to connect a new wallet and therefore disconnect
                    // if a wallet was provided we assume that the user is trying to connect an existing wallet and therefore disconnect if the addresses don't match
                    const addressesMatch =
                        !!constructorWalletAddresses.length &&
                        constructorWalletAddresses.every(
                            (address, i) =>
                                walletConnectSessionAddresses[i] &&
                                blockchainAddressEquals(address, walletConnectSessionAddresses[i]),
                        );

                    if (!addressesMatch || !connectedWithinSession) {
                        await clearWalletConnected(provider);
                        isAlreadyConnected = false;
                    }
                }

                if (!isAlreadyConnected) {
                    await provider.enable().catch(async (err) => {
                        if (isWalletConnectConnected(provider)) {
                            await clearWalletConnected(provider);
                        }
                        throw err;
                    });
                    (window as any).__wallet_connect_within_session__ = true;
                }

                break;
            }

            case "phantom": {
                const { provider } = getPhantomInstance();
                await provider.connect({ onlyIfTrusted: true }).catch(() => {
                    provider.disconnect();
                });
            }
        }
    }

    private async _authenticate(): Promise<ICryptoWalletCreate> {
        await this.ensureClientSideWalletConnection();

        const loggedInWallet = await (async (): Promise<ICryptoWalletCreate> => {
            switch (this.walletProvider) {
                case "torus": {
                    const { client: torusClient, web3Instance } = await getTorusInstance();

                    const addresses: string[] = (await web3Instance.eth.getAccounts()).map(
                        (address) => address.toLowerCase(),
                    );

                    const userInfo: UserInfo = await getTorusUserInfo(torusClient);
                    return {
                        connectionApp: "torus",
                        verifier: userInfo.verifier,
                        verifierId: userInfo.verifierId,
                        verifierEmail: userInfo.email,
                        verifierName: userInfo.name,
                        verifierProfileImage: userInfo.profileImage,
                        cryptoAddresses: await Promise.all(
                            addresses.map(
                                async (address): Promise<ICryptoAddressCreateSigned> => ({
                                    blockchainType: "evm",
                                    address,
                                    ...(await retryWithBackoff(
                                        () => getSignedEvmMessage(web3Instance, address),
                                        // no retry if user closed
                                        (e) => e.message?.toLowerCase().includes("user "),
                                        0,
                                    )),
                                }),
                            ),
                        ),
                    };
                }

                case "coinbase": {
                    const { web3Instance } = getCoinbaseInstance();

                    const addresses: string[] = (await web3Instance.eth.getAccounts()).map(
                        (address) => address.toLowerCase(),
                    );

                    return {
                        connectionApp: "coinbase",
                        cryptoAddresses: await Promise.all(
                            addresses.map(
                                async (address): Promise<ICryptoAddressCreateSigned> => ({
                                    address,
                                    blockchainType: "evm",
                                    ...(await getSignedEvmMessage(web3Instance, address)),
                                }),
                            ),
                        ),
                    };
                }

                case "metamask": {
                    const { web3Instance } = await getMetamaskInstance();

                    const addresses: string[] = (await web3Instance.eth.getAccounts()).map(
                        (address) => address.toLowerCase(),
                    );

                    return {
                        connectionApp: "metamask",
                        cryptoAddresses: await Promise.all(
                            addresses.map(
                                async (address): Promise<ICryptoAddressCreateSigned> => ({
                                    blockchainType: "evm",
                                    address,
                                    ...(await getSignedEvmMessage(web3Instance, address)),
                                }),
                            ),
                        ),
                    };
                }

                case "wallet-connect-v2": {
                    const { web3Instance } = await getWalletConnectV2Instance();

                    // const userInfo = provider.walletMeta;
                    const addresses: string[] = (await web3Instance.eth.getAccounts()).map(
                        (address) => address.toLowerCase(),
                    );

                    return {
                        connectionApp: "wallet-connect-v2",
                        cryptoAddresses: await Promise.all(
                            addresses.map(
                                async (address): Promise<ICryptoAddressCreateSigned> => ({
                                    blockchainType: "evm",
                                    address,
                                    ...(await getSignedEvmMessage(web3Instance, address)),
                                }),
                            ),
                        ),
                    };
                }

                case "wallet-connect": {
                    const { provider, web3Instance } = await getWalletConnectInstance();

                    // const userInfo = provider.walletMeta;
                    const addresses: string[] = (await web3Instance.eth.getAccounts()).map(
                        (address) => address.toLowerCase(),
                    );

                    return {
                        connectionApp: "wallet-connect",
                        // walletConnectApp: userInfo!.url,
                        cryptoAddresses: await Promise.all(
                            addresses.map(
                                async (address): Promise<ICryptoAddressCreateSigned> => ({
                                    blockchainType: "evm",
                                    address,
                                    ...(await getSignedEvmMessage(web3Instance, address)),
                                }),
                            ),
                        ),
                    };
                }

                case "phantom": {
                    const { provider } = getPhantomInstance();
                    try {
                        const resp = await provider.connect();
                        const cryptoAddress = resp.publicKey.toString();
                        return {
                            connectionApp: "phantom",
                            cryptoAddresses: [
                                {
                                    address: cryptoAddress,
                                    blockchainType: "solana",
                                    ...(await getSignedSolanaMessage(provider, cryptoAddress)),
                                },
                            ],
                        };
                    } catch (err) {
                        throw makeErrorSurfaceable(err as string);
                    }
                }
            }
        })();

        return loggedInWallet;
    }
}
